import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { GetApp } from "../App"
import { Block, BlockField, BlockRow } from "../Components/Block"
import { LocalizedMessage } from "../Components/LocalizedMessage"
import { importShipWithStore, useDataStore, useEntity, useRetainedEntities } from "../Data"
import { IShip, IShipImportStatus, Ship } from "../Types"
import { AppRoutes, GetAppView } from "./AppView"
import { logger } from "../App/Logger"

export interface ShipImportViewProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  cancelEdit: any;
  ship?: IShip;
}

export function ShipImportView({ ship }: ShipImportViewProps) {
  const app = GetApp();
  const store = useDataStore();
  const currentShip = useEntity(store, "Ship", ship?.id ?? 0) ?? ship;
  const t = app.getT("ShipImportView");
  const { navigateApp } = GetAppView();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);

  useRetainedEntities(["Ship"], ship != undefined, ship?.id);

  useEffect(() => {
    if (!ship) {
      return;
    }
    if (!store.isQueryFetched("Ship", { shipId: ship.id })) {
      store.fetchQuery("Ship", { shipId: ship.id }).catch(console.error);
    }
  }, [store, ship?.id]);

  const shipData = useMemo(
    () => currentShip ? Ship.parse(currentShip).dataParsed : undefined,
    [currentShip],
  );
  const shipImportStatus = shipData?.importStatus;

  const uploadFile = useCallback(async (file: File) => {
    if (!currentShip) {
      return;
    }
    setUploading(true);
    try {
      const response = await importShipWithStore(store, currentShip.id, file);
      logger.debug("import ship", currentShip.id, response.status);
    } finally {
      setUploading(false);
    }
  }, [store, currentShip]);

  let content = null;
  if (shipImportStatus && shipImportStatus != IShipImportStatus.failed) {
    content = <div><h3 className="text-centered text-success">{t(`status_${shipImportStatus}`)}</h3></div>;
  } else if (uploading) {
    content = <div><h3 className="text-centered text-warning">{t("status_uploading")}</h3></div>;
  } else {
    content = (
      <form>
        {shipImportStatus == IShipImportStatus.failed && <div className="import-error"><h3 className="text-centered text-error">{t("status_failed")}</h3></div>}
        <div className="form-field file-field">
          <label htmlFor="attachment" className="file-field-label" onClick={() => inputRef.current?.click()}><span>{t("file_field_drag_drop")}</span></label>
          <input
            ref={inputRef}
            type="file"
            hidden
            name="attachment"
            accept=".logbook"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) {
                uploadFile(file).catch(console.error);
              }
            }}
          />
        </div>
      </form>
    );
  }

  return (
    <Block className="ShipImportView">
      <h2>{currentShip ? t("title_ship", { ship: currentShip.name }) : t("title")}</h2>
      <LocalizedMessage message={(m) => m.importTrips.text} replace={{ name: currentShip?.name }} className="text-bigger" />
      {content}
      <BlockRow actions={true}>
        <BlockField>
          <button className="button" onClick={() => navigateApp(AppRoutes.accountSettings)}>{t("cancel")}</button>
        </BlockField>
        <BlockField>
          <button className="button default" onClick={() => navigateApp(AppRoutes.ship, { shipId: currentShip?.id || 0 })} disabled={shipImportStatus != IShipImportStatus.complete} type="submit">{t("open")}</button>
        </BlockField>
      </BlockRow>
    </Block>
  );
}
